/**
 * Central registry of TanStack Query keys. Keeping them in one place is what
 * makes cache invalidation after mutations predictable.
 */
import type { CaseAttributeKind, FolderKind } from '@tms/shared';

export const queryKeys = {
  auth: {
    me: ['auth', 'me'] as const,
  },
  users: {
    all: ['users'] as const,
    list: (params: object) => ['users', 'list', params] as const,
  },
  dashboard: ['dashboard'] as const,
  automation: {
    all: ['automation'] as const,
    access: ['automation', 'access'] as const,
    teams: ['automation', 'teams'] as const,
    team: (teamId: string) => ['automation', 'teams', teamId] as const,
    project: (projectId: string) => ['automation', 'project', projectId] as const,
    /** everything about one project: its runs, catalogue, data and notes */
    projectData: (projectId: string, part: string) => ['automation', 'project', projectId, part] as const,
    activeRuns: ['automation', 'runs', 'active'] as const,
    teamRuns: (teamId: string) => ['automation', 'runs', 'team', teamId] as const,
    run: (runId: string) => ['automation', 'run', runId] as const,
    runPart: (runId: string, part: string, extra?: string) =>
      ['automation', 'run', runId, part, extra ?? ''] as const,
    fullRuns: ['automation', 'fullruns'] as const,
    fullRun: (id: string) => ['automation', 'fullruns', id] as const,
    knownDefects: ['automation', 'known-defects'] as const,
    /** TestRun: the account pool and everything read from it */
    testRun: (...parts: string[]) => ['automation', 'testrun', ...parts] as const,
  },
  projects: {
    all: ['projects'] as const,
    list: (params: object) => ['projects', 'list', params] as const,
    detail: (code: string) => ['projects', 'detail', code] as const,
    overview: (code: string) => ['projects', 'overview', code] as const,
    coverage: (code: string, days: number) => ['projects', 'coverage', code, days] as const,
  },
  folders: {
    /** One tree per kind: test case folders and checklist folders are separate. */
    tree: (code: string, kind: FolderKind) => ['folders', code, kind] as const,
  },
  labels: {
    list: (code: string) => ['labels', code] as const,
  },
  environments: {
    list: (code: string) => ['environments', code] as const,
  },
  caseAttributes: {
    /** One list per kind: the layers and the modules of a project. */
    list: (code: string, kind: CaseAttributeKind) => ['case-attributes', code, kind] as const,
  },
  testCases: {
    all: (code: string) => ['test-cases', code] as const,
    list: (code: string, params: object) =>
      ['test-cases', code, 'list', params] as const,
    detail: (code: string, caseKey: string) => ['test-cases', code, 'detail', caseKey] as const,
  },
  checklists: {
    all: (code: string) => ['checklists', code] as const,
    list: (code: string, params: object) =>
      ['checklists', code, 'list', params] as const,
    detail: (code: string, key: string) => ['checklists', code, 'detail', key] as const,
  },
  testRuns: {
    all: (code: string) => ['test-runs', code] as const,
    list: (code: string, params: object) =>
      ['test-runs', code, 'list', params] as const,
    detail: (code: string, key: string) => ['test-runs', code, 'detail', key] as const,
    itemResults: (runId: string, itemId: string) =>
      ['test-runs', 'results', runId, itemId] as const,
  },
  search: (code: string, term: string) => ['search', code, term] as const,
} as const;
